/**
 * Image utility functions for aspect ratio calculations and image processing
 */

/**
 * Calculate aspect ratio from an image file
 * @param file - The image file to analyze
 * @returns Promise<number> - The aspect ratio (width/height)
 */
export const calculateImageAspectRatio = (file: File): Promise<number> => {
  return new Promise((resolve, reject) => {
    if (!file || !file.type.startsWith('image/')) {
      reject(new Error('File is not an image'));
      return;
    }

    const img = new Image();
    const objectUrl = URL.createObjectURL(file);

    img.onload = () => {
      const aspectRatio = img.naturalWidth / img.naturalHeight;
      URL.revokeObjectURL(objectUrl); // Clean up memory
      resolve(aspectRatio);
    };

    img.onerror = () => {
      URL.revokeObjectURL(objectUrl); // Clean up memory
      reject(new Error('Failed to load image'));
    };

    img.src = objectUrl;
  });
};

/**
 * Calculate aspect ratio from an image URL
 * @param url - The image URL to analyze
 * @returns Promise<number> - The aspect ratio (width/height)
 */
export const calculateImageAspectRatioFromUrl = (url: string): Promise<number> => {
  return new Promise((resolve, reject) => {
    const img = new Image();

    img.onload = () => {
      const aspectRatio = img.naturalWidth / img.naturalHeight;
      resolve(aspectRatio);
    };

    img.onerror = () => {
      reject(new Error('Failed to load image from URL'));
    };

    // Enable CORS for cross-origin images if needed
    img.crossOrigin = 'anonymous';
    img.src = url;
  });
};

/**
 * Get image dimensions from a file
 * @param file - The image file to analyze
 * @returns Promise<{width: number, height: number}> - The image dimensions
 */
export const getImageDimensions = (file: File): Promise<{width: number, height: number}> => {
  return new Promise((resolve, reject) => {
    if (!file || !file.type.startsWith('image/')) {
      reject(new Error('File is not an image'));
      return;
    }

    const img = new Image();
    const objectUrl = URL.createObjectURL(file);

    img.onload = () => {
      const dimensions = {
        width: img.naturalWidth,
        height: img.naturalHeight
      };
      URL.revokeObjectURL(objectUrl); // Clean up memory
      resolve(dimensions);
    };

    img.onerror = () => {
      URL.revokeObjectURL(objectUrl); // Clean up memory
      reject(new Error('Failed to load image'));
    };

    img.src = objectUrl;
  });
};

/**
 * Format aspect ratio to a readable string
 * @param aspectRatio - The aspect ratio number
 * @returns string - Formatted aspect ratio (e.g., "16:9", "4:3")
 */
export const formatAspectRatio = (aspectRatio: number): string => {
  // Common aspect ratios
  const commonRatios = [
    { ratio: 16/9, label: '16:9' },
    { ratio: 4/3, label: '4:3' },
    { ratio: 3/2, label: '3:2' },
    { ratio: 1/1, label: '1:1' },
    { ratio: 9/16, label: '9:16' },
    { ratio: 2/3, label: '2:3' },
    { ratio: 3/4, label: '3:4' }
  ];

  // Find the closest common ratio
  const closest = commonRatios.reduce((prev, curr) => {
    return Math.abs(curr.ratio - aspectRatio) < Math.abs(prev.ratio - aspectRatio) ? curr : prev;
  });

  // If very close to a common ratio, return that
  if (Math.abs(closest.ratio - aspectRatio) < 0.01) {
    return closest.label;
  }

  // Otherwise, return the calculated ratio rounded to 2 decimal places
  return `${aspectRatio.toFixed(2)}:1`;
};

/**
 * Get responsive image sizes for different screen breakpoints
 * @param aspectRatio - The original aspect ratio
 * @param maxWidth - Maximum width constraint
 * @returns Object with width/height for different breakpoints
 */
export const getResponsiveImageSizes = (aspectRatio: number, maxWidth: number = 800) => {
  const baseHeight = maxWidth / aspectRatio;
  
  return {
    mobile: {
      width: Math.min(maxWidth, 375),
      height: Math.min(maxWidth, 375) / aspectRatio
    },
    tablet: {
      width: Math.min(maxWidth, 768),
      height: Math.min(maxWidth, 768) / aspectRatio
    },
    desktop: {
      width: maxWidth,
      height: baseHeight
    }
  };
};